import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Chip,
  Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import WifiIcon from '@mui/icons-material/Wifi';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Two rich reference examples with distinct content, banner images, scriptures, and themes
const REFERENCE_EXAMPLES = [
  {
    id: 1,
    title: "21 Days Fasting & Prayer (Spiritual Breakthrough)",
    badgeLabel: "Spiritual Breakthrough",
    totalDays: 21,
    timeSlot: "00:00 - 23:59",
    bannerImage: "/assets/images/example-prayer-banner.png",
    format: "Personal Prayer",
    points: [
      {
        title: "Spiritual Growth & Personal Intimacy",
        description: "Pray that believers grow deeper in their personal relationship with Christ, develop a disciplined daily prayer life, hunger for God's Word, and experience the fullness of the Holy Spirit.",
        scripture: "Grow in grace, and in the knowledge of our Lord and Saviour Jesus Christ. — 2 Peter 3:18",
        image: "/prayforthenation/template1.jpg",
      },
      {
        title: "Persecuted Believers & Global Church",
        description: "Lift up brothers and sisters facing hardship, persecution, and opposition for the Gospel worldwide, asking God for supernatural courage, divine protection, and spiritual boldness.",
        scripture: "Remember them that are in bonds, as bound with them; and them which suffer adversity. — Hebrews 13:3",
        image: "/prayforthenation/template2.jpg",
      }
    ],
    talkItOver: "How has daily personal prayer and fasting transformed your walk with God and family this season?"
  },
  {
    id: 2,
    title: "40 Days Healing, Restoration & Revival",
    badgeLabel: "Healing & Revival",
    totalDays: 40,
    timeSlot: "06:00 - 07:00",
    bannerImage: "/prayforthenation/template4.jpg",
    format: "Group Prayer",
    points: [
      {
        title: "Physical Healing & Brokenhearted Restoration",
        description: "Intercede for those afflicted with sickness, disease, anxiety, or deep emotional wounds, asking God for Christ's supernatural peace, divine intervention, and complete healing in body and soul.",
        scripture: "Heal me, O Lord, and I shall be healed; save me, and I shall be saved: for thou art my praise. — Jeremiah 17:14",
        image: "/prayforthenation/template3.jpg",
      },
      {
        title: "National Awakening & Next-Gen Revival",
        description: "Pray for spiritual awakening across cities, universities, and families. Ask the Lord to turn the hearts of fathers to their children and ignite a fire in the next generation.",
        scripture: "If my people, which are called by my name, shall humble themselves, and pray, and seek my face... — 2 Chronicles 7:14",
        image: "/prayforthenation/template5.jpg",
      }
    ],
    talkItOver: "Share a testimony of God's healing or answered prayer that inspired your church or prayer closet recently."
  }
];

export default function MobilePrayerPreviewModal({
  open,
  onClose,
  prayerTitle = "",
  dayNumber = 1,
  totalDays = 21,
  dayData = {},
  prayerPoints = [],
  timeSlot = "13:30 - 14:00",
  dayWiseData = {},
  fromDate = "",
  mode = "preview", // 'example' | 'preview'
  talkItOver = true,
}) {
  const [activeScreen, setActiveScreen] = useState('overview'); // 'overview' | 'detail'
  const [selectedPointIndex, setSelectedPointIndex] = useState(0);
  const [previewDay, setPreviewDay] = useState(dayNumber || 1);
  const [completedPoints, setCompletedPoints] = useState({});
  const [exampleIndex, setExampleIndex] = useState(0); // 0 = Example 1, 1 = Example 2

  // Reset to overview on open
  React.useEffect(() => {
    if (open) {
      setActiveScreen('overview');
      setPreviewDay(dayNumber || 1);
      setSelectedPointIndex(0);
    }
  }, [open, dayNumber]);

  // In 'example' mode, retrieve active example dataset
  const isExampleMode = mode === 'example';
  const currentExample = REFERENCE_EXAMPLES[exampleIndex] || REFERENCE_EXAMPLES[0];

  // Current day data from admin or fallback
  const activeDayData = (dayWiseData && dayWiseData[previewDay]) || (previewDay === dayNumber ? dayData : {}) || {};

  // Admin prayer points
  const rawAdminPoints = (activeDayData?.prayerPoints && activeDayData.prayerPoints.length > 0)
    ? activeDayData.prayerPoints
    : (previewDay === dayNumber && prayerPoints && prayerPoints.length > 0 ? prayerPoints : []);

  // Filter out any blank points
  const validAdminPoints = (rawAdminPoints || []).filter(
    pt => (pt.title && pt.title.trim() !== '') || (pt.description && pt.description.trim() !== '')
  );

  const activePoints = isExampleMode
    ? currentExample.points
    : validAdminPoints;

  const isUsingDummyData = isExampleMode;

  const totalDaysCount = isExampleMode
    ? currentExample.totalDays
    : Math.max(totalDays || 1, Object.keys(dayWiseData || {}).length || 1, previewDay, 21);

  // Effective title
  const effectivePrayerTitle = isExampleMode
    ? currentExample.title
    : ((activeDayData?.title && activeDayData.title.trim())
      || (prayerTitle && prayerTitle.trim())
      || "Special Prayer");

  // Effective banner image with fallback to guarantee image is always loaded
  const effectiveBannerImage = isExampleMode
    ? currentExample.bannerImage
    : (activeDayData?.image
        ? (typeof activeDayData.image === 'string' ? activeDayData.image : URL.createObjectURL(activeDayData.image))
        : "/assets/images/example-prayer-banner.png");

  const effectiveTimeSlot = isExampleMode
    ? currentExample.timeSlot
    : (timeSlot || "13:30 - 14:00");

  const effectiveTalkItOver = isExampleMode
    ? currentExample.talkItOver
    : "Reflect together on today's devotional prayer focus and share insights with your prayer group.";

  const currentPoint = activePoints[selectedPointIndex] || activePoints[0] || currentExample.points[0];
  const pointImgRaw = currentPoint?.image || currentPoint?.day_img || null;
  const hasPointImage = Boolean(
    pointImgRaw && (
      (typeof pointImgRaw === 'string' && pointImgRaw.trim() !== '' && pointImgRaw !== 'null' && pointImgRaw !== 'undefined') ||
      (typeof File !== 'undefined' && pointImgRaw instanceof File)
    )
  );

  // Helper to format real calendar date (day number + month abbreviation, matching mobile screenshot)
  const getDayDateInfo = (dayNum) => {
    let baseDate = fromDate ? new Date(fromDate) : new Date();
    if (isNaN(baseDate.getTime())) baseDate = new Date();
    const d = new Date(baseDate.getTime() + (dayNum - 1) * 24 * 60 * 60 * 1000);
    const dayStr = d.getDate();
    const monthStr = d.toLocaleString('en-US', { month: 'short' });
    return { dayStr, monthStr };
  };

  // Generate day pills for the SELECT DAY row (6 days window)
  const generateDayPills = () => {
    const pills = [];
    const start = Math.max(1, previewDay - 1);
    const count = Math.min(6, totalDaysCount);
    for (let i = start; i < start + count && i <= totalDaysCount; i++) {
      pills.push(i);
    }
    while (pills.length < Math.min(6, totalDaysCount) && pills[0] > 1) {
      pills.unshift(pills[0] - 1);
    }
    while (pills.length < Math.min(6, totalDaysCount) && pills[pills.length - 1] < totalDaysCount) {
      pills.push(pills[pills.length - 1] + 1);
    }
    return pills;
  };

  const togglePointCompleted = (idx, e) => {
    if (e) e.stopPropagation();
    setCompletedPoints(prev => ({
      ...prev,
      [`${previewDay}_${idx}`]: !prev[`${previewDay}_${idx}`]
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: "44px",
          bgcolor: "#070B11",
          p: 0,
          boxShadow: "0 25px 65px rgba(0,0,0,0.85)",
          border: "8px solid #242E3C",
          overflow: "hidden",
          width: "365px",
          maxWidth: "95vw",
        }
      }}
    >
      {/* ── PHONE TOP STATUS BAR (Android) ── */}
      <Box
        sx={{
          bgcolor: "#0C131A",
          px: 2.5,
          pt: 1.2,
          pb: 0.8,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "#E5E7EB",
          fontSize: "0.82rem",
          fontWeight: 700,
        }}
      >
        <span>2:07</span>
        {/* Punch Hole Camera Cutout */}
        <Box
          sx={{
            width: 14,
            height: 14,
            borderRadius: "50%",
            bgcolor: "#000000",
            border: "2px solid #1E293B",
          }}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
          <WifiIcon sx={{ fontSize: 16 }} />
          <BatteryFullIcon sx={{ fontSize: 18 }} />
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: "#9CA3AF",
              p: 0.2,
              ml: 0.5,
              '&:hover': { color: "#FFFFFF" }
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>

      {/* ── Informational notice for User Example vs Live User Preview ── */}
      {isExampleMode ? (
        <Box
          sx={{
            bgcolor: "#064E3B",
            borderBottom: "1.5px solid #10B981",
            px: 2,
            py: 0.8,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <AutoAwesomeIcon sx={{ fontSize: 18, color: "#34D399 !important", flexShrink: 0 }} />
            <Box>
              <Typography sx={{ color: "#ECFDF5 !important", fontSize: "0.78rem", fontWeight: 700, lineHeight: 1.2 }}>
                User Example {exampleIndex + 1} of 2
              </Typography>
              <Typography sx={{ color: "#A7F3D0 !important", fontSize: "0.68rem", fontWeight: 500 }}>
                {currentExample.badgeLabel}
              </Typography>
            </Box>
          </Box>
          {/* Arrow navigation to see 2nd example */}
          <Box display="flex" alignItems="center" gap={0.5}>
            <Button
              size="small"
              onClick={() => {
                setExampleIndex(prev => (prev === 0 ? 1 : 0));
                setSelectedPointIndex(0);
              }}
              title={exampleIndex === 0 ? "Switch to Example 2" : "Switch to Example 1"}
              sx={{
                bgcolor: "rgba(255,255,255,0.15)",
                color: "#FFFFFF",
                px: 1.2,
                py: 0.4,
                borderRadius: "14px",
                display: "flex",
                alignItems: "center",
                gap: 0.4,
                textTransform: "none",
                fontSize: "0.72rem",
                fontWeight: 700,
                border: "1px solid rgba(255,255,255,0.25)",
                '&:hover': { bgcolor: "rgba(255,255,255,0.28)" }
              }}
            >
              {exampleIndex === 0 ? (
                <>
                  <span>Next Example</span>
                  <ChevronRightIcon sx={{ fontSize: 16 }} />
                </>
              ) : (
                <>
                  <ChevronLeftIcon sx={{ fontSize: 16 }} />
                  <span>1st Example</span>
                </>
              )}
            </Button>
          </Box>
        </Box>
      ) : validAdminPoints.length === 0 ? (
        <Box
          sx={{
            bgcolor: "#1E293B",
            borderBottom: "1.5px solid #F59E0B",
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            gap: 1.2,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 18, color: "#FBBF24 !important", flexShrink: 0 }} />
          <span style={{ color: "#FEF3C7", fontSize: "0.78rem", lineHeight: 1.35, fontWeight: 500 }}>
            <strong style={{ color: "#FBBF24" }}>User Preview:</strong> Showing live content. Add prayer points in the form below to preview them live!
          </span>
        </Box>
      ) : (
        <Box
          sx={{
            bgcolor: "#064E3B",
            borderBottom: "1.5px solid #10B981",
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            gap: 1.2,
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 18, color: "#34D399 !important", flexShrink: 0 }} />
          <span style={{ color: "#ECFDF5", fontSize: "0.78rem", lineHeight: 1.35, fontWeight: 500 }}>
            <strong style={{ color: "#34D399" }}>User Preview:</strong> Showing live prayer content entered by admin ({validAdminPoints.length} point{validAdminPoints.length > 1 ? 's' : ''}).
          </span>
        </Box>
      )}

      {/* ── MOBILE SCREEN VIEW CONTAINER (Languages Bar Completely Removed) ── */}
      <DialogContent
        sx={{
          bgcolor: "#0C131A",
          p: 0,
          height: "640px",
          maxHeight: "80vh",
          overflowY: "auto",
          color: "#FFFFFF !important",
          position: "relative",
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.1)', borderRadius: '4px' },
          '& .MuiTypography-root': {
            color: '#FFFFFF !important',
          },
          '& .MuiTypography-caption': {
            color: '#94A3AF !important',
          },
        }}
      >
        {activeScreen === 'overview' ? (
          /* ══════════════════════════════════════════════════════════
             SCREEN 1: OVERVIEW SCREEN (Exact Match to Mobile App Screenshot 1)
             ══════════════════════════════════════════════════════════ */
          <Box sx={{ p: 2, pb: 4 }}>
            {/* Top Navigation Bar */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center" gap={1.2}>
                <IconButton
                  size="small"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.08)",
                    color: "#FFFFFF",
                    width: 36,
                    height: 36,
                    borderRadius: "12px",
                  }}
                >
                  <ArrowBackIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <Typography
                  variant="h6"
                  fontWeight={800}
                  sx={{
                    fontSize: "1.05rem",
                    color: "#FFFFFF !important",
                    textShadow: "0 1px 3px rgba(0,0,0,0.5)",
                  }}
                >
                  {effectivePrayerTitle}
                </Typography>
              </Box>

              <Box display="flex" alignItems="center" gap={0.8}>
                <IconButton
                  size="small"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.08)",
                    color: "#CBD5E1",
                    width: 36,
                    height: 36,
                    borderRadius: "12px",
                  }}
                >
                  <QrCodeScannerIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.08)",
                    color: "#CBD5E1",
                    width: 36,
                    height: 36,
                    borderRadius: "12px",
                  }}
                >
                  <ShareOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>

            {/* Featured Banner Card with Real Photo & Gradient Overlay */}
            <Box
              sx={{
                position: "relative",
                height: 165,
                borderRadius: "20px",
                overflow: "hidden",
                mb: 2.5,
                boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
                backgroundImage: `linear-gradient(to top, rgba(12, 19, 26, 0.95) 0%, rgba(12, 19, 26, 0.45) 55%, rgba(12, 19, 26, 0.25) 100%), url(${effectiveBannerImage})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                p: 2,
              }}
            >
              {/* Floating Example Arrow Switcher on the Card */}
              {isExampleMode && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.3,
                    bgcolor: "rgba(0, 0, 0, 0.65)",
                    backdropFilter: "blur(8px)",
                    borderRadius: "16px",
                    px: 0.8,
                    py: 0.3,
                    border: "1px solid rgba(255,255,255,0.25)",
                    zIndex: 2,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExampleIndex(prev => (prev === 0 ? 1 : 0));
                      setSelectedPointIndex(0);
                    }}
                    sx={{ p: 0.2, color: "#34D399" }}
                  >
                    <ChevronLeftIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <Typography sx={{ color: "#FFFFFF !important", fontSize: "0.72rem", fontWeight: 800, px: 0.3 }}>
                    {exampleIndex + 1}/2
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExampleIndex(prev => (prev === 0 ? 1 : 0));
                      setSelectedPointIndex(0);
                    }}
                    sx={{ p: 0.2, color: "#34D399" }}
                  >
                    <ChevronRightIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              )}

              <Typography
                variant="h6"
                fontWeight={800}
                sx={{
                  fontSize: "1.05rem",
                  color: "#FFFFFF !important",
                  mb: 0.8,
                  lineHeight: 1.3,
                  textShadow: "0 2px 6px rgba(0,0,0,0.9)",
                }}
              >
                {effectivePrayerTitle}
              </Typography>

              <Chip
                icon={<AutoAwesomeIcon sx={{ fontSize: '13px !important', color: '#6EE7B7 !important' }} />}
                label={`Day ${previewDay} of ${totalDaysCount}`}
                size="small"
                sx={{
                  alignSelf: "flex-start",
                  bgcolor: "rgba(17, 24, 39, 0.85)",
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  height: 24,
                  borderRadius: "12px",
                  backdropFilter: "blur(8px)",
                  border: "1px solid rgba(255,255,255,0.12)",
                }}
              />
            </Box>

            {/* Section: SELECT DAY (Exact date pill style from Screenshot 1) */}
            <Box mb={2.5}>
              <Typography
                variant="caption"
                sx={{
                  color: "#94A3AF",
                  fontWeight: 800,
                  letterSpacing: 0.8,
                  fontSize: "0.75rem",
                  display: "block",
                  mb: 1.2,
                }}
              >
                SELECT DAY
              </Typography>

              <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 0.5 }}>
                {generateDayPills().map((day) => {
                  const isSelected = previewDay === day;
                  const { dayStr, monthStr } = getDayDateInfo(day);
                  return (
                    <Box
                      key={day}
                      onClick={() => setPreviewDay(day)}
                      sx={{
                        width: 52,
                        height: 64,
                        borderRadius: "14px",
                        bgcolor: isSelected ? "#243647" : "#141D26",
                        border: isSelected ? "1.5px solid #38BDF8" : "1px solid rgba(255,255,255,0.06)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        position: "relative",
                        flexShrink: 0,
                        transition: "all 0.2s ease",
                      }}
                    >
                      {/* Cyan dot on active card */}
                      {isSelected && (
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            bgcolor: "#38BDF8",
                            position: "absolute",
                            top: 6,
                            right: 6,
                          }}
                        />
                      )}
                      <Typography
                        variant="body1"
                        fontWeight={800}
                        sx={{
                          color: isSelected ? "#FFFFFF" : "#CBD5E1",
                          fontSize: "1.1rem",
                          lineHeight: 1.1,
                        }}
                      >
                        {dayStr}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: isSelected ? "#94A3B8" : "#64748B",
                          fontWeight: 700,
                          fontSize: "0.7rem",
                        }}
                      >
                        {monthStr}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Section: PRAYER TIME SLOT */}
            <Box mb={2.5}>
              <Typography
                variant="caption"
                sx={{
                  color: "#94A3AF",
                  fontWeight: 800,
                  letterSpacing: 0.8,
                  fontSize: "0.75rem",
                  display: "block",
                  mb: 1.2,
                }}
              >
                PRAYER TIME SLOT
              </Typography>

              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.8,
                  py: 0.9,
                  borderRadius: "12px",
                  bgcolor: "#1C2936",
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 16, color: "#10B981" }} />
                <Typography variant="body2" fontWeight={800} sx={{ color: "#FFFFFF", fontSize: "0.82rem" }}>
                  {effectiveTimeSlot}
                </Typography>
              </Box>
            </Box>

            {/* Section: PRAYER POINTS & THEMES */}
            <Box mb={2}>
              <Typography
                variant="caption"
                sx={{
                  color: "#94A3AF",
                  fontWeight: 800,
                  letterSpacing: 0.8,
                  fontSize: "0.75rem",
                  display: "block",
                  mb: 1.2,
                }}
              >
                PRAYER POINTS & THEMES
              </Typography>

              {/* List of Prayer Point Cards (Shows admin points or dummy data) */}
              <Box display="flex" flexDirection="column" gap={1.2}>
                {activePoints.length === 0 ? (
                  <Box
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      border: '1.5px dashed rgba(255,255,255,0.2)',
                      borderRadius: '16px',
                      bgcolor: 'rgba(255,255,255,0.02)',
                      my: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: '#E2E8F0 !important', fontWeight: 600, mb: 0.5 }}>
                      No Prayer Points Added Yet
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94A3B8 !important', display: 'block', fontSize: '0.78rem' }}>
                      Add prayer points for Day {previewDay} in the form below, then click &quot;User Preview&quot; to review them.
                    </Typography>
                  </Box>
                ) : (
                  activePoints.map((pt, idx) => {
                  const isCompleted = !!completedPoints[`${previewDay}_${idx}`];
                  return (
                    <Box
                      key={idx}
                      onClick={() => {
                        setSelectedPointIndex(idx);
                        setActiveScreen('detail');
                      }}
                      sx={{
                        p: 1.8,
                        borderRadius: "14px",
                        bgcolor: "#111B24",
                        border: isCompleted ? "1.5px solid #059669" : "1.5px solid rgba(13, 148, 136, 0.4)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                        '&:hover': {
                          bgcolor: "#162330",
                          borderColor: "#14B8A6",
                        }
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1.5} sx={{ pr: 1, overflow: "hidden" }}>
                        <CheckCircleIcon
                          onClick={(e) => togglePointCompleted(idx, e)}
                          sx={{
                            fontSize: 20,
                            color: isCompleted ? "#10B981" : "#14B8A6",
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{
                            color: '#FFFFFF',
                            fontSize: '0.92rem',
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            textDecoration: isCompleted ? 'line-through' : 'none',
                            display: 'block',
                          }}
                        >
                          {pt.title || `Prayer Point #${idx + 1}`}
                        </span>
                      </Box>
                      <ChevronRightIcon sx={{ color: "#64748B", fontSize: 20 }} />
                    </Box>
                  );
                }))}
              </Box>

              {/* Talk It Over Card */}
              {Boolean(talkItOver !== false && talkItOver !== 'no') && (
                <Box
                  sx={{
                    mt: 1.5,
                    p: 1.8,
                    borderRadius: "14px",
                    bgcolor: "#14222D",
                    border: "1px solid rgba(56, 189, 248, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    '&:hover': { bgcolor: "#192B3A" }
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <ChatBubbleOutlineIcon sx={{ color: "#38BDF8", fontSize: 22 }} />
                    <Box>
                      <span style={{ color: '#FFFFFF', fontSize: '0.88rem', fontWeight: 800, display: 'block' }}>
                        Talk It Over
                      </span>
                      <span style={{ color: '#94A3B8', fontSize: '0.72rem', display: 'block' }}>
                        {effectiveTalkItOver}
                      </span>
                    </Box>
                  </Box>
                  <ChevronRightIcon sx={{ color: "#64748B", fontSize: 20 }} />
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          /* ══════════════════════════════════════════════════════════
             SCREEN 2: PRAYER POINT DETAILS SCREEN (Exact Match to Screenshot 2)
             ══════════════════════════════════════════════════════════ */
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%", justifyContent: "space-between" }}>
            <Box sx={{ p: 2 }}>
              {/* Top Navigation */}
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <IconButton
                  size="small"
                  onClick={() => setActiveScreen('overview')}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.08)",
                    color: "#FFFFFF",
                    width: 36,
                    height: 36,
                    borderRadius: "12px",
                  }}
                >
                  <ArrowBackIcon sx={{ fontSize: 18 }} />
                </IconButton>

                {/* Logo badge in center */}
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "8px",
                    bgcolor: "rgba(255,255,255,0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.65rem",
                    fontWeight: 800,
                    color: "#CBD5E1",
                  }}
                >
                  GPN
                </Box>

                <Box display="flex" alignItems="center" gap={0.8}>
                  <IconButton
                    size="small"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.08)",
                      color: "#FFFFFF",
                      width: 36,
                      height: 36,
                      borderRadius: "12px",
                      fontSize: "0.8rem",
                      fontWeight: 800,
                    }}
                  >
                    Aa
                  </IconButton>
                  <IconButton
                    size="small"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.08)",
                      color: "#FFFFFF",
                      width: 36,
                      height: 36,
                      borderRadius: "12px",
                    }}
                  >
                    <GroupOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.08)",
                      color: "#FFFFFF",
                      width: 36,
                      height: 36,
                      borderRadius: "12px",
                    }}
                  >
                    <ShareOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </Box>

              {/* Point Image (only rendered if admin added image) */}
              {hasPointImage && (
                <Box
                  sx={{
                    height: 180,
                    borderRadius: "20px",
                    overflow: "hidden",
                    mb: 2.5,
                    background: `url(${typeof pointImgRaw === 'string' ? pointImgRaw : URL.createObjectURL(pointImgRaw)}) center/cover`,
                    border: "1px solid rgba(255,255,255,0.08)",
                    boxShadow: "0 10px 25px rgba(0,0,0,0.4)",
                  }}
                />
              )}

              {/* Point Title */}
              <h3 style={{ color: '#FFFFFF', fontSize: '1.2rem', fontWeight: 800, marginBottom: '8px', marginTop: 0 }}>
                {currentPoint?.title || "Spiritual Growth"}
              </h3>

              {/* Point Scripture (if present) */}
              {currentPoint?.scripture && (
                <Box
                  sx={{
                    mb: 1.5,
                    p: 1.2,
                    borderRadius: "10px",
                    bgcolor: "rgba(56, 189, 248, 0.08)",
                    borderLeft: "3px solid #38BDF8",
                  }}
                >
                  <Typography sx={{ color: "#38BDF8", fontSize: "0.8rem", fontWeight: 700, fontStyle: "italic" }}>
                    📖 {currentPoint.scripture}
                  </Typography>
                </Box>
              )}

              {/* Point Description */}
              <p
                style={{
                  color: '#CBD5E1',
                  fontSize: '0.95rem',
                  lineHeight: 1.6,
                  marginTop: 0,
                }}
              >
                {currentPoint?.description || "Pray that believers grow deeper in their relationship with Christ and hunger for God’s Word"}
              </p>
            </Box>

            {/* Bottom Audio / Player Navigation Bar (Matches Screenshot 2) */}
            <Box
              sx={{
                p: 2,
                pt: 1.5,
                bgcolor: "#0C131A",
                borderTop: "1px solid rgba(255,255,255,0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.2,
              }}
            >
              {/* Play Audio Button */}
              <IconButton
                size="small"
                sx={{
                  bgcolor: "#FFFFFF",
                  color: "#0C131A",
                  width: 40,
                  height: 40,
                  '&:hover': { bgcolor: "#E2E8F0" },
                }}
              >
                <PlayArrowIcon sx={{ fontSize: 24 }} />
              </IconButton>

              {/* Previous Point */}
              <IconButton
                size="small"
                disabled={selectedPointIndex === 0}
                onClick={() => setSelectedPointIndex(prev => Math.max(0, prev - 1))}
                sx={{
                  bgcolor: "#1C2733",
                  color: "#FFFFFF",
                  width: 40,
                  height: 40,
                  '&.Mui-disabled': { color: "#475569", bgcolor: "#141D26" },
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: 22 }} />
              </IconButton>

              {/* Center "Prayer" / "Prayed" Pill */}
              <Button
                variant="contained"
                onClick={() => togglePointCompleted(selectedPointIndex)}
                sx={{
                  flex: 1,
                  height: 40,
                  borderRadius: "20px",
                  bgcolor: completedPoints[`${previewDay}_${selectedPointIndex}`] ? "#059669" : "#22303F",
                  color: "#FFFFFF",
                  fontWeight: 800,
                  fontSize: "0.85rem",
                  textTransform: "none",
                  boxShadow: "none",
                  '&:hover': { bgcolor: completedPoints[`${previewDay}_${selectedPointIndex}`] ? "#047857" : "#2B3C4E" },
                }}
              >
                {completedPoints[`${previewDay}_${selectedPointIndex}`] ? "✔ Prayed" : "Prayer"}
              </Button>

              {/* Next Point */}
              <IconButton
                size="small"
                disabled={selectedPointIndex >= activePoints.length - 1}
                onClick={() => setSelectedPointIndex(prev => Math.min(activePoints.length - 1, prev + 1))}
                sx={{
                  bgcolor: "#1C2733",
                  color: "#FFFFFF",
                  width: 40,
                  height: 40,
                  '&.Mui-disabled': { color: "#475569", bgcolor: "#141D26" },
                }}
              >
                <ChevronRightIcon sx={{ fontSize: 22 }} />
              </IconButton>
            </Box>
          </Box>
        )}
      </DialogContent>

      {/* ── PHONE BOTTOM HOME INDICATOR BAR ── */}
      <Box
        sx={{
          bgcolor: "#0C131A",
          py: 1,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: 110,
            height: 4,
            bgcolor: "rgba(255,255,255,0.7)",
            borderRadius: 2,
          }}
        />
      </Box>
    </Dialog>
  );
}
